// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  Table,
  PreferancesDatasProps,
  TableActionType,
} from '@wajeez-web/components/data-table';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { DataTable, Dropdown, Pagination } from '@wajeez-web/components/mui';
import { Server } from '@wajeez-web/core/api-base';
import { contentEntityDataType } from '@wajeez-web/stores/content';
import { environment } from '@wajeez-web/core/utils';
import { DotsHorizontalIcon } from '@heroicons/react/outline';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { FormField } from '@wajeez-web/components/form-builder';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { InputSelect, PopOver } from '@wajeez-web/components/ui';
import { useAuthSelector } from '@wajeez-web/stores/auth';

export type Header = {
  value: string | undefined;
  label: string | number | React.ReactElement | undefined;
  selected: boolean;
};

type RowType = { id: string; [key: string]: string | JSX.Element | undefined };

export type FilterOption = {
  key: string | undefined;
  label: string | undefined;
  dataType: Server.DataType | undefined;
  operator?: Server.ComparisonType | undefined;
  value?: string | undefined;
  onChange?: (value: string | undefined) => void;
};

interface DataTableWrapperProps {
  data: contentEntityDataType | null;
  onHeaderManage: (header: Header[] | undefined) => void;
  onPaginationChange: (pagination: {
    pageNumber: number;
    pageSize: number;
  }) => void;
  sort:
    | {
        property?: number | undefined;
        orderByDirection?: Server.OrderByDirection | undefined;
      }
    | undefined;
  onSort: (sort: {
    property: number | undefined;
    orderByDirection: Server.OrderByDirection;
  }) => void;
  onFilter: (filters: FilterOption[], pagination: Pagination) => void;
  isLoading: boolean;
  preferences: PreferancesDatasProps | undefined;
  setPreferences: React.Dispatch<
    React.SetStateAction<PreferancesDatasProps | null>
  >;
  extraActions?: {
    label: string;
    onClick: () => void;
    Icon?: JSX.Element;
  }[];
  rowActions?: (row: RowType) => TableActionType[];
  columnsSelectors?: (row: RowType) => { [key: number]: JSX.Element };
  defaultFilters?: {
    key: string;
    operator: Server.ComparisonType;
    dataType: Server.DataType;
    label: string;
    fieldMeta: FormField;
    onChange?: (value: any) => void;
  }[];
  version?: 'oldDataTable' | 'muiDataTable';
  forcePaginationReset?: boolean;
  onForcePaginationReset?: () => void;
}

export const DataTableWrapper = ({
  data,
  onHeaderManage,
  onPaginationChange,
  sort,
  onSort,
  onFilter,
  isLoading,
  extraActions,
  setPreferences,
  preferences,
  rowActions,
  columnsSelectors,
  defaultFilters,
  version,
  forcePaginationReset,
  onForcePaginationReset,
}: DataTableWrapperProps) => {
  const permissions = useAuthSelector(({ auth }) => auth.permission);

  const renderDataTable = () => {
    switch (version || environment.REACT_APP_DataTable) {
      case 'oldDataTable':
        return (
          <Table
            isUpdating={isLoading}
            isLoading={isLoading}
            setPreferances={setPreferences}
            PreferancesData={preferences || undefined}
            tableData={data}
            columnsSelectors={(id, row) => {
              return columnsSelectors ? columnsSelectors(row) : {};
            }}
            tableHeaderActions={(columns) => {
              return (
                extraActions?.map((a) => {
                  return { ...a, type: 'action' };
                }) || []
              );
            }}
            tableActions={(id, row) => {
              return (rowActions && rowActions(row)) || [];
            }}
            defultFilterscomponents={defaultFilters?.map((d) => {
              if (d.fieldMeta.fieldType === Server.FieldType.Select) {
                return (
                  <PopOver
                    label="Content Type"
                    name="ContentType"
                    shape="circle"
                    panelStyle="w-52"
                  >
                    <InputSelect
                      isMenuOpen={true}
                      options={d.fieldMeta.fieldOptions.selectOptions}
                      onChange={(name, val: any) => {
                        preferences &&
                          setPreferences({
                            ...preferences,
                            filteringOperations: [
                              {
                                cretiria: val.value,
                                comparisonType: 0,
                                property: 2,
                              },
                            ],
                          });

                        d.onChange && d.onChange(val.value);
                      }}
                      name="ContentType"
                    />
                  </PopOver>
                );
              } else {
                return <></>;
              }
            })}
          />
        );
      case 'muiDataTable':
        return (
          <DataTable
            dataTableData={data}
            onHeaderManage={onHeaderManage}
            onPaginationChange={onPaginationChange}
            sort={sort}
            onSort={onSort}
            onFilter={onFilter}
            isLoading={isLoading}
            extraActions={extraActions}
            mapRowsCustomized={(rows) => {
              return rows?.map((r) => {
                if (columnsSelectors) {
                  return {
                    ...r,
                    ...columnsSelectors(r),
                  };
                } else {
                  return r;
                }
              });
            }}
            rowActions={rowActions}
            renderRowActions={(row) => {
              if (rowActions?.length) {
                return (
                  <div className="w-full flex justify-center items-center">
                    <Dropdown
                      id={`action-${row[1]}`}
                      items={
                        rowActions
                          ? rowActions(row).map((a) => {
                              return permissions?.includes(a.permission)
                                ? {
                                    label: a.title,
                                    // eslint-disable-next-line @typescript-eslint/no-empty-function
                                    onClick: a.action || (() => {}),
                                  }
                                : null;
                            })
                          : []
                      }
                      label={
                        <div style={{ color: '#343a40' }}>
                          <DotsHorizontalIcon
                            style={{ width: '28px', height: '24px' }}
                          />
                        </div>
                      }
                      buttonStyle={{
                        width: '34px',
                        minWidth: '34px',
                        paddingRight: '2px',
                        paddingLeft: '2px',
                        color: 'black',
                      }}
                    />
                  </div>
                );
              } else {
                return <></>;
              }
            }}
            defaultFilters={defaultFilters}
          />
        );
      default:
        return null;
    }
  };

  return <div className="w-full">{renderDataTable()}</div>;
};
